import { useQuery } from '@tanstack/react-query';
import { procedureApi } from '../services/procedureApi';
import { Stethoscope } from 'lucide-react';

export type GroupFilter = { type: 'all' } | { type: 'group'; groupId: number } | { type: 'unmapped' };

interface ServiceGroupsListProps {
  readonly selectedFilter: GroupFilter;
  readonly onSelectFilter: (filter: GroupFilter) => void;
  readonly procedureCounts: Map<number | null, number>;
}

export default function ServiceGroupsList({ selectedFilter, onSelectFilter, procedureCounts }: Readonly<ServiceGroupsListProps>) {
  const { data: groups, isLoading, error } = useQuery({
    queryKey: ['serviceGroups'],
    queryFn: () => procedureApi.getServiceGroups(),
  });

  if (isLoading) return <div className="procedures-loading"><p>Loading...</p></div>;
  if (error) return <div className="procedures-empty"><p>Error loading groups</p></div>;

  const mappedCount = Array.from(procedureCounts.entries())
    .filter(([key]) => key !== null)
    .reduce((sum, [, count]) => sum + count, 0);
  const unmappedCount = procedureCounts.get(null) ?? 0;

  return (
    <>
      <div className="groups-sidebar-header">
        <Stethoscope size={16} />
        Service Groups
      </div>
      <ul className="groups-sidebar-list">
        <li>
          <button
            type="button"
            className={`group-btn ${selectedFilter.type === 'all' ? 'active' : ''}`}
            onClick={() => onSelectFilter({ type: 'all' })}
          >
            <span className="group-btn-name">All Procedures</span>
            <span className="group-btn-count">{mappedCount}</span>
          </button>
        </li>
        <li><div className="groups-sidebar-divider" /></li>
        {groups?.map((group) => (
          <li key={group.id}>
            <button
              type="button"
              className={`group-btn ${selectedFilter.type === 'group' && selectedFilter.groupId === group.id ? 'active' : ''}`}
              onClick={() => onSelectFilter({ type: 'group', groupId: group.id })}
            >
              <span className="group-btn-name">{group.name}</span>
              <span className="group-btn-count">{procedureCounts.get(group.id) ?? 0}</span>
            </button>
          </li>
        ))}
        <li><div className="groups-sidebar-divider" /></li>
        <li>
          <button
            type="button"
            className={`group-btn ${selectedFilter.type === 'unmapped' ? 'active' : ''}`}
            onClick={() => onSelectFilter({ type: 'unmapped' })}
          >
            <span className="group-btn-name">Unmapped</span>
            <span className="group-btn-count">{unmappedCount}</span>
          </button>
        </li>
      </ul>
    </>
  );
}
